import React from 'react';
import { MapPin, CheckCircle, Clock } from 'lucide-react';

const ServiceAreas = () => {
  const activeAreas = [
    { 
      name: 'Panaon Island', 
      status: 'active', 
      locations: ['Liloan', 'Pintuyan', 'San Ricardo'],
      clients: '450+' 
    },
    { 
      name: 'Maasin City', 
      status: 'active',
      locations: ['Abgao', 'Pinaskuhan', 'Nonok Norte', 'San Agustin', 'Hinapu Gamay', 'Hinapung Daku', 'Libertad', 'Ka Floro', 'Baugo', 'Canserong', 'Lunas', 'Campo', 'Caiyanohan', 'Canluguc'],
      clients: '1,200+' 
    }
  ];

  const expandingAreas = [
    { name: 'Panaon Island: San Francisco', status: 'phase2', eta: 'Q2 2025' },
    { name: 'Limasawa Island', status: 'phase2', eta: 'Q2 2025' },
    { name: 'Matalom', status: 'phase2', eta: 'Q3 2025' }
  ];

  return (
    <section className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-4">
            Coverage Map
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Expanding Connectivity
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Bringing carrier-grade fiber internet to underserved communities across the Visayas
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Map Visual */}
          <div className="relative">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 h-full">
              {/* Simplified map illustration */}
              <div className="relative h-96 bg-slate-950 rounded-xl overflow-hidden">
                {/* Map background */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 opacity-50"></div>
                
                {/* Active service markers - Panaon Island area */}
                <div className="absolute top-1/2 left-1/3 animate-pulse">
                  <div className="relative">
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <div className="absolute -top-1 -left-1 w-6 h-6 bg-green-500/30 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-green-400 font-semibold">
                      Panaon
                    </div>
                  </div>
                </div>
                
                <div className="absolute top-1/3 right-1/3 animate-pulse" style={{ animationDelay: '0.5s' }}>
                  <div className="relative">
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <div className="absolute -top-1 -left-1 w-6 h-6 bg-green-500/30 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-green-400 font-semibold">
                      Maasin
                    </div>
                  </div>
                </div>
                
                {/* Phase 2 markers */}
                <div className="absolute top-1/4 left-1/4">
                  <div className="relative">
                    <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-yellow-400 font-semibold">
                      Limasawa
                    </div>
                  </div>
                </div>
                
                <div className="absolute bottom-1/3 left-1/2">
                  <div className="relative">
                    <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-yellow-400 font-semibold">
                      San Francisco
                    </div>
                  </div>
                </div>
                
                <div className="absolute top-2/3 right-1/4">
                  <div className="relative">
                    <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs text-yellow-400 font-semibold">
                      Matalom
                    </div>
                  </div>
                </div>

                {/* Connection lines between active areas */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  <line x1="33%" y1="50%" x2="66%" y2="33%" stroke="#10b981" strokeWidth="2" strokeDasharray="5,5" opacity="0.5"/>
                </svg>
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center space-x-6 mt-6">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-400">Active Network</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-400">Phase 2 Expansion</span>
                </div>
              </div>
            </div>
          </div>

          {/* Service Areas List */}
          <div className="space-y-6">
            {/* Active Areas */}
            <div>
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <CheckCircle className="text-green-500 mr-2" size={24} />
                Active Service Areas
              </h3>
              <div className="space-y-3">
                {activeAreas.map((area, index) => (
                  <div 
                    key={index}
                    className="bg-slate-800/50 border border-green-500/30 rounded-xl p-4 hover:border-green-500/50 transition-all duration-200"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start space-x-3">
                        <MapPin className="text-green-500 mt-1 flex-shrink-0" size={20} />
                        <div>
                          <h4 className="text-white font-semibold">{area.name}</h4>
                          <p className="text-sm text-gray-400 mb-2">{area.clients} active subscribers</p>
                          {area.locations && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {area.locations.slice(0, 6).map((loc, idx) => (
                                <span key={idx} className="text-xs bg-slate-700/50 text-cyan-400 px-2 py-0.5 rounded">
                                  {loc}
                                </span>
                              ))}
                              {area.locations.length > 6 && (
                                <span className="text-xs text-gray-500 px-2 py-0.5">
                                  +{area.locations.length - 6} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-lg flex-shrink-0">
                        <span className="text-green-400 text-sm font-semibold">Live</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expanding Areas */}
            <div>
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <Clock className="text-yellow-500 mr-2" size={24} />
                Network Expansion
              </h3>
              <div className="space-y-3">
                {expandingAreas.map((area, index) => (
                  <div 
                    key={index}
                    className="bg-slate-800/50 border border-yellow-500/30 hover:border-yellow-500/50 rounded-xl p-4 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <MapPin className="text-yellow-500" size={20} />
                        <div>
                          <h4 className="text-white font-semibold">{area.name}</h4>
                          <p className="text-sm text-gray-400">Target launch: {area.eta}</p>
                        </div>
                      </div>
                      <div className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                        <span className="text-yellow-400 text-sm font-semibold">Phase 2</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-6 mt-6">
              <h4 className="text-white font-semibold mb-2">Want FiberStream in Your Area?</h4>
              <p className="text-gray-400 text-sm mb-4">
                Contact us to discuss bringing carrier-grade fiber internet to your community through our franchise program.
              </p>
              <a 
                href="/franchise" 
                className="inline-block px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors text-sm font-semibold"
              >
                Explore Franchise Options
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
